import { useEffect, useRef, useState } from "react";
import Table from "../common/Table/Table";
import { adminApplicationsTableColumns } from "../../config/tableColumns";
import { useNavigate } from "react-router-dom";
import api from "../../api/ApiService";
import {
  applicationStageList,
  applicationStageMap,
  applicationStatusMap,
  incentiveClaimTypesList,
} from "../../config/applicationStatusConfig";
import { formatDate } from "../../utils/utils";
import Button from "../common/Button";
import { getStoredItem } from "../../config/storageUtils";
import { installmentsList } from "../../config/appliedSchemesConfig";
import { uttarakhandDistrictList } from "../../config/districtConfig";
import DownloadExcel from "../common/DownloadExcel";
import ApplicationPrint from "../common/ApplicationPrint";
import Generating from "../common/Generating";

const AdminApplications = ({ title, status, stage }) => {
  const [applications, setApplications] = useState([]);
  const [categoryId, setCategoryId] = useState("1");
  const navigate = useNavigate();
  const [totalEntries, setTotalEntries] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [loadingPdf, setLoadingPdf] = useState(false);
  const pdfRef = useRef(null);
  const [pdfData, setPdfData] = useState(null);

  const handleView = (row) => {
    navigate("/admin/application-view", {
      state: { applicationData: row, fromPage: location.pathname },
    });
  };

  const downloadPdf = (row) => {
    const applicationDetailsSampleData = {
      "Tracking Number": row.tracking_number || "",
      "CAF ID": row.caf_id || "N/A",
      "CAF Applied Date": row.caf_applied_date || "N/A",
      "CAF Approved Date": row.caf_approved_date || "N/A",
      District: row.district || "N/A",
      "Application Number": row.application_number || "",
      "Applied Date": formatDate(row.created_at) || "",
      "Applied Scheme": row.category || "N/A",
      Address: row.address || "N/A",
      "Phone Number": row.mobile_number || "N/A",
      "Email Address": row.email || "N/A",
    };

    setPdfData({
      applicationId: row.application_id,
      incentiveClaimId: row.applied_incetive_claim_value,
      installmentType: row.application_type,
      userId: row.user_id, // admin can download for any user
      applicationDetailsSampleData, // Pass the sample data to ApplicationPrint
    });
    setLoadingPdf(true);
  };

  const fetchApplications = async (page = 1, perPage = 10, search = "") => {
    try {
      // build query params dynamically
      const queryParams = new URLSearchParams({
        perPage,
        page,
        search,
      });

      if (selectedDistrict) {
        queryParams.append("district", selectedDistrict);
      }

      if (status?.length) {
        queryParams.append("status", status.join(","));
      }

      if (stage?.length) {
        queryParams.append("stage", stage.join(","));
      }

      const response = await api.get(
        `/admin/categories/applications/list?${queryParams.toString()}`,
      );
      const data = response.data.data || [];

      if (data.length > 0) {
        const claimTypeMap = Object.fromEntries(
          incentiveClaimTypesList.map((t) => [String(t.value), t.label]),
        );
        const installmentMap = Object.fromEntries(
          installmentsList.map((i) => [String(i.value), i.name]),
        );

        // filter + map data
        const filtered = data.map((item, index) => {
          const claimType = incentiveClaimTypesList.find(
            (t) => t.value === String(item.invest_claim_id),
          );

          const matchedStatus = Object.values(applicationStatusMap).find(
            (statusObj) => statusObj.value === String(item.status),
          );

          const claimLabel =
            claimTypeMap[String(item.invest_claim_id)] || "Unknown";
          const installmentName = installmentMap[String(item.application_type)]
            ? `(${installmentMap[String(item.application_type)]})`
            : "";

          return {
            sr_no: index + 1,
            applicant_name: item.users_list.name,
            application_id: item.id,
            email: item.users_list.email,
            tracking_number: item.tracking_number,
            updated_at: formatDate(item.updated_at),
            applied_incetive_claim: `${claimLabel} ${installmentName}`.trim(),
            applied_incetive_claim_value: item.invest_claim_id,
            status: matchedStatus?.label || "Unknown",
            status_value: item.status,
            user_id: item.user_id,
            application_stage: item.application_stage,
            application_number: item.application_number,
            created_at: item.created_at,
            category: item.sub_category_name || "N/A",
            mobile_number: item.users_list.mobile_number || "N/A",
            caf_id: item.caf_id,
            district: item.users_list.district_name || "N/A",
            address: item.users_list.address || "N/A",
            application_type: item.application_type,
            application_stage_name:
              applicationStageList.find(
                (stage) => stage.value === String(item.application_stage),
              )?.label || "Nodal",
            caf_approved_date: item.caf_approved_date || "N/A",
            caf_applied_date: item.caf_applied_date || "N/A",
            certificate_url: item.certificate_url,
          };
        });

        setApplications(filtered);
        setTotalEntries(response.data.total);
      } else {
        setApplications([]);
        setTotalEntries(0);
      }
    } catch (error) {
      console.error("Failed to fetch data", error);
    }
  };

  useEffect(() => {
    fetchApplications(currentPage, rowsPerPage, searchQuery);
  }, [currentPage, rowsPerPage, searchQuery, selectedDistrict]);

  return (
    <>
      <div className="flex justify-end h-10 mb-4">
        <select
          value={selectedDistrict}
          onChange={(e) => {
            setSelectedDistrict(e.target.value);
            setCurrentPage(1); // reset pagination
          }}
          className="border rounded px-2 py-1 text-sm mr-4"
        >
          <option value="">All Districts</option>
          {uttarakhandDistrictList.map((d) => (
            <option key={d.id} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>

        <DownloadExcel
          apiEndpoint="/admin/excel/applications"
          queryParams={{
            search: searchQuery,
            district: selectedDistrict,
            status: status?.join(","),
            stage: stage?.join(","),
          }}
        />
      </div>
      <div className="flex justify-between items-center h-12 nav-bg p-3 text-white font-semibold rounded-tr rounded-tl">
        <span className="text-white">{title}</span>
      </div>

      <Table
        columns={adminApplicationsTableColumns(handleView, downloadPdf)}
        data={applications}
        totalEntries={totalEntries}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={setRowsPerPage}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        rowsPerPageOptions={[5, 10, 20, 30, 100]}
      />

      {loadingPdf && <Generating text="Generating PDF, please wait..." />}

      {pdfData && (
        <ApplicationPrint
          ref={pdfRef}
          applicationId={pdfData.applicationId}
          incentiveClaimId={pdfData.incentiveClaimId}
          installmentType={pdfData.installmentType}
          userId={pdfData.userId}
          isUserSide={false}
          onDone={() => {
            setLoadingPdf(false); // ✅ loader removed
            setPdfData(null); // ✅ cleanup
          }}
          applicationDetailsSampleData={pdfData.applicationDetailsSampleData} // Pass the sample data to ApplicationPrint
        />
      )}
    </>
  );
};

export default AdminApplications;
